import type { SamenvattingGroep } from "./Samenvatting";

/**
 * De groepen omzetten naar een kruistabel: rijen tegen kolommen.
 *
 * <p>Zo staat het in het statistiekenscherm dat dit vervangt: de reeks als rij, de periodes als
 * kolommen, en in elke cel het getal. Dat leest anders dan een lange lijst waarin dezelfde
 * vestiging vijf keer onder elkaar staat met telkens een ander jaartal ernaast.
 *
 * <p><b>De periode is de kolom en niet de rij.</b> Dat is niet willekeurig: een tijdreeks lees je
 * van links naar rechts, en zo staat 2024 naast 2025 in plaats van eronder. De server geeft de
 * periode als eerste groepering, dus die wordt hier de kolom.
 *
 * <p>De volgorde van beide assen blijft zoals de server hem gaf. Die sorteert op de groepeerwaarde,
 * dus maanden staan op maandvolgorde. Hier alsnog op het getal sorteren zou van een tijdreeks een
 * ranglijst maken.
 */
export interface Kruistabel {
  /** De koppen van de kolommen — de eerste as, meestal de periode. */
  kolommen: string[];
  /** Per rij het label van de tweede as en de waarde per kolom; ontbreekt een cel, dan null. */
  rijen: { label: string; cellen: (SamenvattingGroep | null)[] }[];
}

export function kruistabelVan(groepen: SamenvattingGroep[]): Kruistabel {
  const kolommen: string[] = [];
  const rijLabels: string[] = [];
  // Sleutel op beide labels: bij twee assen is één combinatie precies één groep.
  const cel = new Map<string, SamenvattingGroep>();

  for (const g of groepen) {
    const kolom = g.labels[0] ?? "";
    const rij = g.labels[1] ?? "";
    if (!kolommen.includes(kolom)) kolommen.push(kolom);
    if (!rijLabels.includes(rij)) rijLabels.push(rij);
    cel.set(rij + " " + kolom, g);
  }

  return {
    kolommen,
    // Een combinatie die niet voorkomt wordt null en geen nul: "die vestiging had dat jaar geen
    // dieren" en "we weten het niet" zien er als 0 hetzelfde uit, en dat verschil doet ertoe zodra
    // iemand een lege rij als een echte nul overneemt.
    rijen: rijLabels.map((label) => ({
      label,
      cellen: kolommen.map((k) => cel.get(label + " " + k) ?? null),
    })),
  };
}
